/**
 * Content Library Page
 * Manage flipbooks, images, videos, and files
 * Marketo-style asset management with grid/list views
 */

import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getContentItems,
  deleteContentItem,
  bulkDeleteContentItems,
  bulkUpdateVisibility,
} from "../services/content.service";
import {
  getFolders,
  createFolder,
  renameFolder,
  deleteFolder,
  moveContentToFolder,
  moveFolder,
} from "../services/contentFolders.service";
import { QUERY_KEYS } from "../utils/constants";
import { useToast } from "../components/ui/Toast";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Card from "../components/ui/Card";
import ContentUploadModal from "../components/content/ContentUploadModal";
import ContentDetailPanel from "../components/content/ContentDetailPanel";
import ContentGridItem from "../components/content/ContentGridItem";
import ContentListItem from "../components/content/ContentListItem";
import WebpageChatForm from "../components/content/WebpageChatForm";
import type {
  ContentItem,
  ContentType,
  ContentFilters,
  ContentFolder,
} from "../types/content.types";
import {
  ContentType as ContentTypeEnum,
  CONTENT_TYPE_OPTIONS,
} from "../types/content.types";
import {
  MagnifyingGlassIcon,
  Squares2X2Icon,
  ListBulletIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  FolderIcon,
  FolderPlusIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/outline";
import { FolderIcon as FolderSolidIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";

type ViewMode = "grid" | "list";

export default function Content() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<ContentType | "all">("all");
  const [featuredFilter, setFeaturedFilter] = useState<
    "all" | "featured" | "not_featured"
  >("all");
  const [showFolderPanel, setShowFolderPanel] = useState(true);
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);

  // Folder management state
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newSubfolderParentId, setNewSubfolderParentId] = useState<
    string | null
  >(null);
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [renamingFolderName, setRenamingFolderName] = useState("");
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [folderMenuId, setFolderMenuId] = useState<string | null>(null);
  const [moveToFolderItemId, setMoveToFolderItemId] = useState<string | null>(
    null,
  );
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

  // Selection state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);

  // Modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState<ContentType | null>(null);
  const [showWebpageChatForm, setShowWebpageChatForm] = useState(false);

  // Build filters
  const filters: ContentFilters = useMemo(() => {
    const f: ContentFilters = {};
    if (searchTerm) {
      f.search = searchTerm;
    }
    if (typeFilter !== "all") {
      f.content_type = [typeFilter as ContentType];
    }
    if (featuredFilter === "featured") {
      f.is_featured = true;
    } else if (featuredFilter === "not_featured") {
      f.is_featured = false;
    }
    if (activeFolderId !== null) {
      f.folder_id = activeFolderId;
    }
    return f;
  }, [searchTerm, typeFilter, featuredFilter, activeFolderId]);

  // Fetch content items
  const {
    data: contentItems = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [QUERY_KEYS.CONTENT, filters],
    queryFn: () => getContentItems(filters),
  });

  // Fetch folders
  const { data: folders = [] } = useQuery({
    queryKey: [QUERY_KEYS.CONTENT, "folders"],
    queryFn: getFolders,
  });

  // Build folder tree
  const folderTree = useMemo(() => {
    const rootFolders: ContentFolder[] = [];
    const childMap = new Map<string, ContentFolder[]>();

    for (const folder of folders) {
      if (folder.parent_folder_id) {
        const children = childMap.get(folder.parent_folder_id) || [];
        children.push(folder);
        childMap.set(folder.parent_folder_id, children);
      } else {
        rootFolders.push(folder);
      }
    }

    return { rootFolders, childMap };
  }, [folders]);

  // Folder mutations
  const createFolderMutation = useMutation({
    mutationFn: ({
      name,
      parentId,
    }: {
      name: string;
      parentId?: string | null;
    }) => createFolder(name, parentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONTENT, "folders"],
      });
      setShowNewFolderInput(false);
      setNewSubfolderParentId(null);
      setNewFolderName("");
      showToast("success", "Folder created");
    },
    onError: (error: Error) => {
      showToast("error", `Failed to create folder: ${error.message}`);
    },
  });

  const renameFolderMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      renameFolder(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONTENT, "folders"],
      });
      setRenamingFolderId(null);
      showToast("success", "Folder renamed");
    },
    onError: (error: Error) => {
      showToast("error", `Failed to rename folder: ${error.message}`);
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: deleteFolder,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONTENT, "folders"],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTENT] });
      if (activeFolderId) {
        setActiveFolderId(null);
      }
      showToast("success", "Folder deleted");
    },
    onError: (error: Error) => {
      showToast("error", `Failed to delete folder: ${error.message}`);
    },
  });

  const moveToFolderMutation = useMutation({
    mutationFn: ({
      contentItemId,
      folderId,
    }: {
      contentItemId: string;
      folderId: string | null;
    }) => moveContentToFolder(contentItemId, folderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTENT] });
      setMoveToFolderItemId(null);
      showToast("success", "Content moved");
    },
    onError: (error: Error) => {
      showToast("error", `Failed to move content: ${error.message}`);
    },
  });

  const moveFolderMutation = useMutation({
    mutationFn: ({
      folderId,
      newParentId,
    }: {
      folderId: string;
      newParentId: string | null;
    }) => moveFolder(folderId, newParentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONTENT, "folders"],
      });
      showToast("success", "Folder moved");
    },
    onError: (error: Error) => {
      showToast("error", `Failed to move folder: ${error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteContentItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTENT] });
      showToast("success", "Content deleted successfully");
      setSelectedItem(null);
    },
    onError: (error: Error) => {
      showToast("error", `Failed to delete: ${error.message}`);
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: bulkDeleteContentItems,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTENT] });
      showToast("success", `Deleted ${selectedItems.size} items`);
      setSelectedItems(new Set());
    },
    onError: (error: Error) => {
      showToast("error", `Failed to delete: ${error.message}`);
    },
  });

  // Bulk visibility mutation
  const bulkVisibilityMutation = useMutation({
    mutationFn: ({ ids, isPublic }: { ids: string[]; isPublic: boolean }) =>
      bulkUpdateVisibility(ids, isPublic),
    onSuccess: (_, { isPublic }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTENT] });
      showToast(
        "success",
        `Updated ${selectedItems.size} items to ${isPublic ? "public" : "private"}`,
      );
      setSelectedItems(new Set());
    },
    onError: (error: Error) => {
      showToast("error", `Failed to update visibility: ${error.message}`);
    },
  });

  // Handlers
  const handleSelectItem = (id: string, selected: boolean) => {
    const newSelection = new Set(selectedItems);
    if (selected) {
      newSelection.add(id);
    } else {
      newSelection.delete(id);
    }
    setSelectedItems(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === contentItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(contentItems.map((item) => item.id)));
    }
  };

  const handleViewItem = (item: ContentItem) => {
    setSelectedItem(item);
  };

  const handleDeleteItem = async (id: string) => {
    if (!confirm("Are you sure you want to delete this content?")) {
      return;
    }
    await deleteMutation.mutateAsync(id);
  };

  const handleShareItem = (item: ContentItem) => {
    // Generate the public viewer URL
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/view/${item.id}`;

    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        showToast("success", "Share link copied to clipboard");
      })
      .catch(() => {
        showToast("error", "Failed to copy link");
      });
  };

  const handleBulkDelete = async () => {
    if (
      !confirm(`Are you sure you want to delete ${selectedItems.size} items?`)
    ) {
      return;
    }
    await bulkDeleteMutation.mutateAsync(Array.from(selectedItems));
  };

  const handleBulkVisibility = async (isPublic: boolean) => {
    await bulkVisibilityMutation.mutateAsync({
      ids: Array.from(selectedItems),
      isPublic,
    });
  };

  const toggleFolderExpanded = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) {
      return;
    }
    createFolderMutation.mutate({
      name: newFolderName.trim(),
      parentId: newSubfolderParentId,
    });
  };

  const handleRenameFolder = () => {
    if (!renamingFolderId || !renamingFolderName.trim()) {
      return;
    }
    renameFolderMutation.mutate({
      id: renamingFolderId,
      name: renamingFolderName.trim(),
    });
  };

  // Check if targetId is a descendant of sourceId (prevents circular drops)
  const isDescendant = useCallback(
    (sourceId: string, targetId: string): boolean => {
      const children = folderTree.childMap.get(sourceId) || [];
      for (const child of children) {
        if (child.id === targetId) {
          return true;
        }
        if (isDescendant(child.id, targetId)) {
          return true;
        }
      }
      return false;
    },
    [folderTree.childMap],
  );

  const handleFolderDrop = useCallback(
    (draggedFolderId: string, targetFolderId: string | null) => {
      setDragOverFolderId(null);

      // Can't drop on itself
      if (draggedFolderId === targetFolderId) {
        return;
      }

      // Can't drop a parent onto its own descendant
      if (targetFolderId && isDescendant(draggedFolderId, targetFolderId)) {
        return;
      }

      // Find current parent — skip if already there
      const draggedFolder = folders.find((f) => f.id === draggedFolderId);
      if (!draggedFolder) {
        return;
      }
      const currentParent = draggedFolder.parent_folder_id || null;
      if (currentParent === targetFolderId) {
        return;
      }

      moveFolderMutation.mutate({
        folderId: draggedFolderId,
        newParentId: targetFolderId,
      });

      // Auto-expand the target so the user sees the result
      if (targetFolderId) {
        setExpandedFolders((prev) => new Set([...prev, targetFolderId]));
      }
    },
    [folders, isDescendant, moveFolderMutation],
  );

  const handleDeleteFolder = (folderId: string) => {
    if (
      !confirm(
        "Delete this folder? Content items will be moved to the root level.",
      )
    ) {
      return;
    }
    deleteFolderMutation.mutate(folderId);
  };

  const handleUploadClick = (type?: ContentType) => {
    // Handle webpage chat separately with its own form
    if (type === ContentTypeEnum.WEBPAGE_CHAT) {
      setShowWebpageChatForm(true);
      return;
    }
    setUploadType(type || null);
    setShowUploadModal(true);
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    setUploadType(null);
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTENT] });
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="p-4">
        <Card className="bg-red-50 border-red-200">
          <p className="text-red-700">
            Error loading content: {(error as Error).message}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-none p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold text-gray-900">
            Content Library
          </h1>
          <div className="flex items-center gap-2">
            {/* Add Content Dropdown */}
            <div className="relative group">
              <Button onClick={() => handleUploadClick()}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Content
              </Button>
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <div className="py-1">
                  {CONTENT_TYPE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleUploadClick(option.value)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) =>
              setTypeFilter(e.target.value as ContentType | "all")
            }
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            {CONTENT_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Featured Filter */}
          <select
            value={featuredFilter}
            onChange={(e) =>
              setFeaturedFilter(
                e.target.value as "all" | "featured" | "not_featured",
              )
            }
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Content</option>
            <option value="featured">Featured</option>
            <option value="not_featured">Not Featured</option>
          </select>

          {/* Folder Panel Toggle */}
          <button
            onClick={() => setShowFolderPanel(!showFolderPanel)}
            className={clsx(
              "p-2 rounded-lg border transition-colors",
              showFolderPanel
                ? "bg-blue-50 border-blue-300 text-blue-600"
                : "border-gray-300 text-gray-600 hover:bg-gray-100",
            )}
            title="Toggle folder panel"
          >
            <FolderIcon className="h-5 w-5" />
          </button>

          {/* View Mode Toggle */}
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("grid")}
              className={clsx(
                "p-2 transition-colors",
                viewMode === "grid"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-100",
              )}
              title="Grid view"
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={clsx(
                "p-2 transition-colors",
                viewMode === "list"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-100",
              )}
              title="List view"
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedItems.size > 0 && (
          <div className="mt-4 flex items-center gap-4 p-3 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium text-blue-700">
              {selectedItems.size} item{selectedItems.size !== 1 ? "s" : ""}{" "}
              selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleBulkVisibility(true)}
                disabled={bulkVisibilityMutation.isPending}
              >
                <EyeIcon className="h-4 w-4 mr-1" />
                Make Public
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleBulkVisibility(false)}
                disabled={bulkVisibilityMutation.isPending}
              >
                <EyeSlashIcon className="h-4 w-4 mr-1" />
                Make Private
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isPending}
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedItems(new Set())}
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Folder Panel */}
        {showFolderPanel && (
          <div className="w-56 border-r border-gray-200 bg-gray-50 flex flex-col overflow-hidden flex-shrink-0">
            <div className="p-3 border-b border-gray-200 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Folders
              </span>
              <button
                onClick={() => {
                  setShowNewFolderInput(true);
                  setNewSubfolderParentId(null);
                  setNewFolderName("");
                }}
                className="p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                title="New Folder"
              >
                <FolderPlusIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-2 space-y-0.5">
              {/* All Content (root) */}
              {/* All Content (root) — also a drop target for moving folders to root */}
              <button
                onClick={() => setActiveFolderId(null)}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverFolderId("__root__");
                }}
                onDragLeave={() => setDragOverFolderId(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  const draggedId = e.dataTransfer.getData(
                    "application/x-folder-id",
                  );
                  if (draggedId) {
                    handleFolderDrop(draggedId, null);
                  }
                }}
                className={clsx(
                  "w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors",
                  dragOverFolderId === "__root__"
                    ? "bg-blue-200 ring-2 ring-blue-400 ring-inset"
                    : activeFolderId === null
                      ? "bg-blue-100 text-blue-700 font-medium"
                      : "text-gray-700 hover:bg-gray-200",
                )}
              >
                <FolderSolidIcon className="h-4 w-4 flex-shrink-0" />
                All Content
              </button>

              {/* Folder Tree */}
              {folderTree.rootFolders.map((folder) => (
                <FolderTreeNode
                  key={folder.id}
                  folder={folder}
                  childMap={folderTree.childMap}
                  depth={0}
                  activeFolderId={activeFolderId}
                  expandedFolders={expandedFolders}
                  folderMenuId={folderMenuId}
                  renamingFolderId={renamingFolderId}
                  renamingFolderName={renamingFolderName}
                  dragOverFolderId={dragOverFolderId}
                  onSelect={setActiveFolderId}
                  onToggleExpand={toggleFolderExpanded}
                  onMenuToggle={(id) =>
                    setFolderMenuId(folderMenuId === id ? null : id)
                  }
                  onStartRename={(id, name) => {
                    setRenamingFolderId(id);
                    setRenamingFolderName(name);
                    setFolderMenuId(null);
                  }}
                  onRename={(name) => {
                    setRenamingFolderName(name);
                  }}
                  onConfirmRename={handleRenameFolder}
                  onCancelRename={() => setRenamingFolderId(null)}
                  onDelete={handleDeleteFolder}
                  onNewSubfolder={(parentId) => {
                    setShowNewFolderInput(true);
                    setNewSubfolderParentId(parentId);
                    setNewFolderName("");
                    setFolderMenuId(null);
                    setExpandedFolders((prev) => new Set([...prev, parentId]));
                  }}
                  onDragOver={setDragOverFolderId}
                  onDrop={handleFolderDrop}
                />
              ))}

              {/* New folder input */}
              {showNewFolderInput && !newSubfolderParentId && (
                <div className="flex items-center gap-1 px-2 py-1">
                  <FolderIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    autoFocus
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleCreateFolder();
                      }
                      if (e.key === "Escape") {
                        setShowNewFolderInput(false);
                        setNewFolderName("");
                      }
                    }}
                    onBlur={() => {
                      if (newFolderName.trim()) {
                        handleCreateFolder();
                      } else {
                        setShowNewFolderInput(false);
                      }
                    }}
                    placeholder="Folder name"
                    className="flex-1 min-w-0 px-1.5 py-0.5 text-sm border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-auto p-4">
          {contentItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <p className="text-lg mb-4">No content found</p>
              <Button onClick={() => handleUploadClick()}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Your First Content
              </Button>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {contentItems.map((item) => (
                <ContentGridItem
                  key={item.id}
                  item={item}
                  selected={selectedItems.has(item.id)}
                  onSelect={(selected) => handleSelectItem(item.id, selected)}
                  onClick={() => handleViewItem(item)}
                  onDelete={() => handleDeleteItem(item.id)}
                  onShare={() => handleShareItem(item)}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {/* List Header */}
              <div className="flex items-center px-4 py-2 bg-gray-50 rounded-lg text-sm font-medium text-gray-500">
                <div className="w-8">
                  <input
                    type="checkbox"
                    checked={selectedItems.size === contentItems.length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </div>
                <div className="flex-1">Title</div>
                <div className="w-24 text-center">Type</div>
                <div className="w-32">Created By</div>
                <div className="w-24 text-center">Views</div>
                <div className="w-24 text-center">Visibility</div>
                <div className="w-32 text-right">Actions</div>
              </div>
              {/* List Items */}
              {contentItems.map((item) => (
                <ContentListItem
                  key={item.id}
                  item={item}
                  selected={selectedItems.has(item.id)}
                  onSelect={(selected) => handleSelectItem(item.id, selected)}
                  onClick={() => handleViewItem(item)}
                  onDelete={() => handleDeleteItem(item.id)}
                  onShare={() => handleShareItem(item)}
                />
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="mt-4 text-sm text-gray-500 text-center">
            Showing {contentItems.length} item
            {contentItems.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Detail Panel */}
        {selectedItem && (
          <ContentDetailPanel
            item={selectedItem}
            onClose={() => setSelectedItem(null)}
            onUpdate={() => {
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTENT] });
            }}
            onDelete={() => handleDeleteItem(selectedItem.id)}
          />
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <ContentUploadModal
          initialType={uploadType}
          onClose={() => {
            setShowUploadModal(false);
            setUploadType(null);
          }}
          onComplete={handleUploadComplete}
        />
      )}

      {/* Webpage Chat Form */}
      {showWebpageChatForm && (
        <WebpageChatForm
          onClose={() => setShowWebpageChatForm(false)}
          onComplete={() => {
            setShowWebpageChatForm(false);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONTENT] });
          }}
        />
      )}

      {/* Move to Folder Modal */}
      {moveToFolderItemId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-80 max-h-96 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Move to Folder</h3>
            </div>
            <div className="flex-1 overflow-auto p-2">
              <button
                onClick={() => {
                  moveToFolderMutation.mutate({
                    contentItemId: moveToFolderItemId,
                    folderId: null,
                  });
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100"
              >
                <FolderIcon className="h-4 w-4" />
                No Folder (root)
              </button>
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => {
                    moveToFolderMutation.mutate({
                      contentItemId: moveToFolderItemId,
                      folderId: folder.id,
                    });
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-gray-700 hover:bg-gray-100"
                  style={{
                    paddingLeft: folder.parent_folder_id ? "2rem" : undefined,
                  }}
                >
                  <FolderIcon className="h-4 w-4" />
                  {folder.name}
                </button>
              ))}
            </div>
            <div className="p-3 border-t border-gray-200">
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => setMoveToFolderItemId(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================
// FOLDER TREE NODE COMPONENT
// =====================================================

interface FolderTreeNodeProps {
  folder: ContentFolder;
  childMap: Map<string, ContentFolder[]>;
  depth: number;
  activeFolderId: string | null;
  expandedFolders: Set<string>;
  folderMenuId: string | null;
  renamingFolderId: string | null;
  renamingFolderName: string;
  dragOverFolderId: string | null;
  onSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onMenuToggle: (id: string) => void;
  onStartRename: (id: string, name: string) => void;
  onRename: (name: string) => void;
  onConfirmRename: () => void;
  onCancelRename: () => void;
  onDelete: (id: string) => void;
  onNewSubfolder: (parentId: string) => void;
  onDragOver: (folderId: string | null) => void;
  onDrop: (draggedFolderId: string, targetFolderId: string | null) => void;
}

function FolderTreeNode({
  folder,
  childMap,
  depth,
  activeFolderId,
  expandedFolders,
  folderMenuId,
  renamingFolderId,
  renamingFolderName,
  dragOverFolderId,
  onSelect,
  onToggleExpand,
  onMenuToggle,
  onStartRename,
  onRename,
  onConfirmRename,
  onCancelRename,
  onDelete,
  onNewSubfolder,
  onDragOver,
  onDrop,
}: FolderTreeNodeProps) {
  const children = childMap.get(folder.id) || [];
  const hasChildren = children.length > 0;
  const isExpanded = expandedFolders.has(folder.id);
  const isActive = activeFolderId === folder.id;
  const isRenaming = renamingFolderId === folder.id;
  const showMenu = folderMenuId === folder.id;
  const isDragOver = dragOverFolderId === folder.id;

  return (
    <div>
      <div
        draggable={!isRenaming}
        onDragStart={(e) => {
          e.dataTransfer.setData("application/x-folder-id", folder.id);
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "move";
          onDragOver(folder.id);
        }}
        onDragLeave={(e) => {
          e.stopPropagation();
          // Only clear if leaving this node (not entering a child)
          const related = e.relatedTarget as HTMLElement | null;
          if (!e.currentTarget.contains(related)) {
            onDragOver(null);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const draggedId = e.dataTransfer.getData("application/x-folder-id");
          if (draggedId) {
            onDrop(draggedId, folder.id);
          }
        }}
        className={clsx(
          "group flex items-center gap-1 rounded text-sm transition-colors relative",
          isDragOver
            ? "bg-blue-200 ring-2 ring-blue-400 ring-inset"
            : isActive
              ? "bg-blue-100 text-blue-700 font-medium"
              : "text-gray-700 hover:bg-gray-200",
        )}
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
      >
        {/* Expand/collapse */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) {
              onToggleExpand(folder.id);
            }
          }}
          className={clsx(
            "p-0.5 rounded flex-shrink-0",
            hasChildren ? "hover:bg-gray-300/50" : "invisible",
          )}
        >
          <ChevronRightIcon
            className={clsx(
              "h-3 w-3 transition-transform",
              isExpanded && "rotate-90",
            )}
          />
        </button>

        {/* Folder button */}
        {isRenaming ? (
          <input
            type="text"
            autoFocus
            value={renamingFolderName}
            onChange={(e) => onRename(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onConfirmRename();
              }
              if (e.key === "Escape") {
                onCancelRename();
              }
            }}
            onBlur={() => {
              if (renamingFolderName.trim()) {
                onConfirmRename();
              } else {
                onCancelRename();
              }
            }}
            className="flex-1 min-w-0 px-1.5 py-0.5 text-sm border border-blue-300 rounded focus:ring-1 focus:ring-blue-500"
          />
        ) : (
          <button
            onClick={() => onSelect(folder.id)}
            className="flex-1 flex items-center gap-1.5 py-1.5 pr-1 min-w-0 text-left"
          >
            <FolderIcon className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{folder.name}</span>
          </button>
        )}

        {/* Context menu button */}
        {!isRenaming && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle(folder.id);
            }}
            className={clsx(
              "p-0.5 rounded flex-shrink-0 transition-opacity",
              showMenu ? "opacity-100" : "opacity-0 group-hover:opacity-100",
              "hover:bg-gray-300/50",
            )}
          >
            <EllipsisHorizontalIcon className="h-3.5 w-3.5" />
          </button>
        )}

        {/* Context menu dropdown */}
        {showMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => onMenuToggle(folder.id)}
            />
            <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
              <button
                onClick={() => onNewSubfolder(folder.id)}
                className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                New Subfolder
              </button>
              <button
                onClick={() => onStartRename(folder.id, folder.name)}
                className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                Rename
              </button>
              <button
                onClick={() => {
                  onMenuToggle(folder.id);
                  onDelete(folder.id);
                }}
                className="w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          </>
        )}
      </div>

      {/* Children */}
      {isExpanded &&
        children.map((child) => (
          <FolderTreeNode
            key={child.id}
            folder={child}
            childMap={childMap}
            depth={depth + 1}
            activeFolderId={activeFolderId}
            expandedFolders={expandedFolders}
            folderMenuId={folderMenuId}
            renamingFolderId={renamingFolderId}
            renamingFolderName={renamingFolderName}
            dragOverFolderId={dragOverFolderId}
            onSelect={onSelect}
            onToggleExpand={onToggleExpand}
            onMenuToggle={onMenuToggle}
            onStartRename={onStartRename}
            onRename={onRename}
            onConfirmRename={onConfirmRename}
            onCancelRename={onCancelRename}
            onDelete={onDelete}
            onNewSubfolder={onNewSubfolder}
            onDragOver={onDragOver}
            onDrop={onDrop}
          />
        ))}
    </div>
  );
}
